import { constructSortFn } from '@tanstack/react-table';
import type { MemberSortFn } from './memberDataset.types';
import type { FieldFormat } from './memberViewFormat';
import { parseDateOnly } from './memberViewFormat';
import { PRIORITY_TAGS, type PriorityTag } from './priorityTags';

/*
 * Ascending comparators for dataset fields. None of them sees a missing value: columns hand the
 * table `undefined` for null, and the table's own `sortUndefined` (`'last'`, unless the field says
 * otherwise) places those rows before a comparator runs.
 */

const compareNumbers = (a: number, b: number) => a - b;

/** Calendar dates, compared as dates. An unparseable date sorts as the earliest. */
export const sortFnDate: MemberSortFn = constructSortFn({
  resolveDataValue: (value: string) => parseDateOnly(value)?.getTime() ?? Number.NEGATIVE_INFINITY,
  sort: compareNumbers,
});

export const sortFnNumber: MemberSortFn = constructSortFn({ sort: compareNumbers });

/** False before true, so ascending puts "No" first. */
export const sortFnBoolean: MemberSortFn = constructSortFn({
  sort: (a: boolean, b: boolean) => Number(a) - Number(b),
});

/**
 * Priority tags, compared A–Z on each row's tags. A tag's place in `PRIORITY_TAGS` stands in for its
 * label: that list is in the labels' alphabetical order (tested). A row with no tags never reaches
 * this — its field's value is missing, so it sorts last both ways.
 */
export const sortFnTags: MemberSortFn = constructSortFn({
  resolveDataValue: (tags: readonly PriorityTag[]) =>
    tags.map((tag) => PRIORITY_TAGS.indexOf(tag)).sort(compareNumbers),
  sort: (a: number[], b: number[]) => {
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      if (a[i] !== b[i]) return a[i] - b[i];
    }
    return a.length - b.length;
  },
});

/** Text in the reader's locale, ignoring case and accents, with digit runs compared as numbers. */
export function sortFnText(locale: string): MemberSortFn {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  return constructSortFn({ sort: (a: unknown, b: unknown) => collator.compare(String(a), String(b)) });
}

/** The comparator a field's display format implies. */
export function sortFnForFormat(format: FieldFormat | undefined, locale: string): MemberSortFn {
  switch (format) {
    case 'date':
      return sortFnDate;
    case 'currency':
    case 'number':
      return sortFnNumber;
    case 'yesBlank':
    case 'yesNoBlank':
      return sortFnBoolean;
    default:
      return sortFnText(locale);
  }
}
